import { useState } from 'react';
import { X, ImagePlus, FileText, Star } from 'lucide-react';
import { MediaLibraryModal } from './MediaLibraryModal';
import { AiImagePanel, type AiImageContext } from './AiImagePanel';

type MediaFolder = 'properties' | 'agents' | 'posts' | 'settings' | 'pages' | 'general';

interface Props {
  value: string[];
  onChange: (urls: string[]) => void;
  multiple?: boolean;
  folder?: MediaFolder;
  accept?: 'images' | 'pdf';
  variant?: 'default' | 'large';
  // Si se pasa, el campo es elegible para generación de imagen con IA (Fase 3).
  aiImage?: AiImageContext;
}

export function MediaPicker({
  value,
  onChange,
  multiple = true,
  folder = 'general',
  accept = 'images',
  variant = 'default',
  aiImage,
}: Props) {
  const [open, setOpen] = useState(false);
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  // Defensa: el campo solo maneja URLs (strings). Si llega un valor sucio
  // (p. ej. datos legacy donde un campo de objetos se mapeó a este picker),
  // descartamos lo que no sea string en vez de reventar en url.toLowerCase().
  value = Array.isArray(value) ? value.filter((u): u is string => typeof u === 'string') : [];

  const remove = (url: string) => onChange(value.filter(u => u !== url));

  // Reorder: the array order *is* the display order; index 0 is the main image.
  const move = (from: number, to: number) => {
    if (to < 0 || to >= value.length || from === to) return;
    const next = [...value];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    onChange(next);
  };

  const handleSelect = (urls: string[]) => {
    if (multiple) {
      const merged = [...value, ...urls.filter(u => !value.includes(u))];
      onChange(merged);
    } else {
      onChange(urls.slice(0, 1));
    }
  };

  const isPdf = (url: string) => url.toLowerCase().endsWith('.pdf');

  // Reordering only makes sense for multi-image fields with more than one item.
  const canReorder = multiple && accept !== 'pdf' && value.length > 1;

  return (
    <>
      <div className="space-y-3">
        {canReorder && (
          <p className="text-sm text-ui-text-subtle">
            Arrastra para reordenar · la primera es la foto principal
          </p>
        )}

        {/* Selected items */}
        {value.length > 0 && (
          <div className={variant === 'large' ? "grid grid-cols-1 gap-4" : "grid grid-cols-1 sm:grid-cols-2 gap-4"}>
            {value.map((url, i) => (
              <div
                key={url}
                draggable={canReorder}
                onDragStart={canReorder ? () => setDragIndex(i) : undefined}
                onDragOver={canReorder ? (e) => e.preventDefault() : undefined}
                onDrop={canReorder ? (e) => {
                  e.preventDefault();
                  if (dragIndex !== null) move(dragIndex, i);
                  setDragIndex(null);
                } : undefined}
                onDragEnd={canReorder ? () => setDragIndex(null) : undefined}
                className={`
                  group relative rounded-lg overflow-hidden border border-ui-border shadow-sm
                  ${variant === 'large' ? 'aspect-[3/1] bg-ui-surface-2' : 'aspect-video bg-ui-surface'}
                  ${canReorder ? 'cursor-move' : ''}
                  ${dragIndex === i ? 'opacity-40 ring-2 ring-ui-accent' : ''}
                `}
              >
                {isPdf(url) ? (
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex flex-col items-center justify-center w-full h-full gap-2 text-ui-text-muted hover:text-ui-text transition-colors"
                  >
                    <FileText size={variant === 'large' ? 32 : 28} />
                    <span className="text-xs truncate max-w-[90%]">{url.split('/').pop()}</span>
                  </a>
                ) : (
                  <div className={`w-full h-full flex items-center justify-center ${variant === 'large' ? 'p-6' : ''}`}>
                    <img
                      src={url}
                      alt=""
                      draggable={false}
                      className={`w-full h-full ${variant === 'large' ? 'object-contain' : 'object-cover'}`}
                    />
                  </div>
                )}

                {/* Main-image badge / promote button */}
                {canReorder && (
                  i === 0 ? (
                    <span className="absolute top-2 left-2 px-2 py-0.5 bg-ui-accent text-ui-text-inverse text-xs font-medium uppercase tracking-wider rounded-md shadow-sm pointer-events-none">
                      Principal
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => move(i, 0)}
                      title="Hacer principal"
                      className="absolute top-2 left-2 flex items-center gap-1 px-2 py-0.5 bg-ui-surface-2/90 text-ui-text-muted hover:text-ui-text text-xs uppercase tracking-wider rounded-md opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                    >
                      <Star size={11} /> Principal
                    </button>
                  )
                )}

                <button
                  type="button"
                  onClick={() => remove(url)}
                  className="absolute top-2 right-2 p-1.5 bg-ui-danger/90 text-ui-text-inverse rounded-md opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                >
                  <X size={16} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Add / change button — always visible so single-value fields are replaceable */}
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex items-center gap-2 px-3 py-2 border border-dashed border-ui-border rounded-lg text-sm text-ui-text-muted hover:text-ui-text hover:border-ui-border-strong transition-colors"
        >
          {accept === 'pdf' ? <FileText size={16} /> : <ImagePlus size={16} />}
          {accept === 'pdf'
            ? (value.length > 0 ? 'Cambiar PDF' : 'Agregar PDF')
            : (multiple || value.length === 0 ? 'Agregar imagen' : 'Cambiar imagen')}
        </button>

        {aiImage && accept === 'images' && (
          <AiImagePanel context={aiImage} onPick={(url) => handleSelect([url])} />
        )}
      </div>

      <MediaLibraryModal
        open={open}
        onClose={() => setOpen(false)}
        onSelect={handleSelect}
        multiple={multiple}
        folder={folder}
        accept={accept}
        currentSelected={value}
      />
    </>
  );
}
